import { takeLatest, call, put, select, takeEvery } from 'redux-saga/effects'
import * as actions from './actions'
import { startLoading, stopLoading, enqueueSnackbar } from '../root/actions'
import axios from 'axios'
import { getSagasMsgs } from '../root/selectors'
import { DEFAULT_TAKE } from './reducers'
import { prepareReportsFiltersAsParams } from 'web/src/utils'

export const watchReportsSaga = [
    takeLatest(actions.REPORTS_REQUEST, requestReports),
    takeLatest(actions.FETCH_MORE_REPORTS, requestMoreReports),
    takeLatest(actions.REPORT_REQUEST, requestReport),
    takeLatest(
        actions.REPORT_SHARING_HISTORY_REQUEST,
        requestReportSharingHistorySaga
    ),
    takeLatest(actions.COMMENT_POST_REQUEST, postComment),
    takeLatest(actions.COMMENT_DELETE_REQUEST, deleteComment),
    takeLatest(actions.COMMENT_EDIT_REQUEST, editComment),
    takeLatest(actions.COMMENT_REQUEST, requestComment),
    takeEvery(
        actions.GET_IMAGES_FROM_COMMENT_BY_COMMENT_ID,
        getImagesByCommentIdSaga
    ),
    takeLatest(actions.DELETE_IMAGES_FROM_COMMENT, deleteCommentImagesByIdSaga)
]

const getSkip = state => state.reports.skip

function* requestReports(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    const { filters } = action
    try {
        yield put(startLoading())
        const response = yield call(fetchReports, {
            skip: 0,
            take: DEFAULT_TAKE,
            ...filters
        })
        yield put(actions.reportsRequestSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(actions.reportsRequestError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getReportsEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

function* requestMoreReports(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    const { filters } = action
    const skip = yield select(getSkip)
    try {
        const response = yield call(fetchReports, {
            ...filters,
            skip: skip,
            take: DEFAULT_TAKE
        })
        yield put(actions.reportsRequestMoreSuccess(response))
    } catch (ex) {
        yield put(actions.reportsRequestMoreError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getReportsEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function fetchReports(params) {
    const filtersToParams = prepareReportsFiltersAsParams(params)
    const response = await axios.get(`${process.env.API_URL}/reports`, {
        params: { ...filtersToParams }
    })
    return response.data
}

function* requestReport(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(fetchReport, action.reportId)
        yield put(actions.reportRequestSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(actions.reportRequestError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getReportEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function fetchReport(reportId) {
    const response = await axios.get(
        `${process.env.API_URL}/reports/${reportId}`
    )
    return response.data
}
function* requestReportSharingHistorySaga(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(fetchReportSharingHistory, action.reportId)
        yield put(actions.reportSharingHistoryRequestSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(actions.reportSharingHistoryRequestError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getReportSharingHistoryEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function fetchReportSharingHistory(reportId) {
    const response = await axios.get(
        `${process.env.API_URL}/reports/sharing-history/${reportId}`
    )
    return response.data
}

function* postComment(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    yield put(actions.setCommentImagesLoader('post'))

    try {
        const response = yield call(postCommentRequest, action.data)
        yield put(actions.commentPostSuccess(response))
        const fetch = yield call(fetchComment, action.data.comment)
        yield put(actions.requestCommentSuccess(fetch))
        yield put(actions.setCommentImagesLoader(false))

        yield put(
            enqueueSnackbar({
                message: sagasMsgs.commentAddRes,
                options: {
                    variant: 'success'
                }
            })
        )
    } catch (error) {
        yield put(actions.setCommentImagesLoader(false))
        yield put(actions.commentPostError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.commentAddEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function postCommentRequest(action) {
    const formData = new FormData()

    formData.append('comment[entity_id]', action.comment.entity_id)
    formData.append('comment[entity_type]', 'reports')
    formData.append('comment[customer_id]', action.comment.customer_id)
    formData.append('comment[text]', action.comment.text)

    action.comment.images?.forEach(image => {
        formData.append(`images`, image)
    })

    const response = await axios.post(
        `${process.env.API_URL}/comments`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' }
        }
    )

    return response.data
}

function* deleteComment(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(actions.setCommentImagesLoader(action.data?.commentId))
        const response = yield call(deleteCommentRequest, action)
        yield put(actions.deleteCommentSuccess(response))
        const fetch = yield call(fetchComment, action.data)
        yield put(actions.requestCommentSuccess(fetch))
        yield put(actions.setCommentImagesLoader(false))

        yield put(
            enqueueSnackbar({
                message: sagasMsgs.commentDeleteRes,
                options: {
                    variant: 'success'
                }
            })
        )
    } catch (ex) {
        yield put(actions.setCommentImagesLoader(false))
        yield put(actions.deleteCommentError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.commentDeleteEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function deleteCommentRequest(action) {
    const response = await axios.delete(
        `${process.env.API_URL}/comments/${action.data.commentId}`,
        action
    )
    return response.data
}

function* editComment(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(actions.setCommentImagesLoader(action.data?.commentId))
        const response = yield call(editCommentRequest, action.data)
        yield put(actions.editCommentSuccess(response))

        // refetch
        const responseImage = yield call(getImagesByCommentId, response.id)
        yield put(actions.setCommentImagesLoader(false))
        yield put(
            actions.setImagesForComments({
                [action.data.commentId]: responseImage
            })
        )

        const fetch = yield call(fetchComment, action.data)
        yield put(actions.requestCommentSuccess(fetch))
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.commentEditRes,
                options: {
                    variant: 'success'
                }
            })
        )
    } catch (ex) {
        yield put(actions.setCommentImagesLoader(false))
        yield put(actions.editCommentError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.commentEditEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function editCommentRequest(action) {
    const formData = new FormData()

    formData.append('comment[text]', action.comment.text)
    formData.append('comment[customer_id]', action.comment.customer_id)

    action.comment.deletedPictureKeys?.forEach(image =>
        formData.append('deletedPictureKeys[]', image.key)
    )

    action.comment.images?.forEach(image => formData.append(`images`, image))

    const response = await axios.patch(
        `${process.env.API_URL}/comments/${action.commentId}`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' }
        }
    )

    return response.data
}

function* deleteCommentImagesByIdSaga(action) {
    const sagasMsgs = yield select(getSagasMsgs)

    try {
        const response = yield call(deleteCommentImagesById, action)

        // refetch
        const responseImage = yield call(getImagesByCommentId, response.id)

        yield put(
            actions.setImagesForComments({
                [action.image.commentId]: responseImage
            })
        )
    } catch (ex) {
        yield put(actions.requestCommentError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getCommentsEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function deleteCommentImagesById(action) {
    const formData = new FormData()

    formData.append('comment[text]', action.image.text)
    formData.append('deletedPictureKeys[]', action.image.image.key)

    const response = await axios.patch(
        `${process.env.API_URL}/comments/${action.image.commentId}`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' }
        }
    )

    return response.data
}

function* requestComment(action) {
    const sagasMsgs = yield select(getSagasMsgs)

    try {
        const response = yield call(fetchComment, action.comments)

        yield put(actions.requestCommentSuccess(response))
    } catch (ex) {
        yield put(actions.requestCommentError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getCommentsEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

function* getImagesByCommentIdSaga(action) {
    const sagasMsgs = yield select(getSagasMsgs)

    try {
        yield put(actions.setCommentImagesLoader(action.commentId))
        const response = yield call(getImagesByCommentId, action.commentId)

        yield put(
            actions.setImagesForComments({
                [action.commentId]: response
            })
        )
        yield put(actions.setCommentImagesLoader(false))
    } catch (ex) {
        yield put(actions.setCommentImagesLoader(false))
        yield put(actions.requestCommentError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getCommentsEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function fetchComment(action) {
    const response = await axios.get(`${process.env.API_URL}/comments`, {
        params: {
            entity_id: action.entity_id,
            entity_type: action.entity_type
        }
    })

    return response.data
}

async function getImagesByCommentId(commentId) {
    const response = await axios.get(
        `${process.env.API_URL}/comments/${commentId}/pictures`
    )

    const data = response.data || []

    return data
}
